import React, { useState, useRef, useEffect } from "react";

// Modules
import { useSelector, useDispatch } from "react-redux";

// App
import { getOnlineNode } from "../../core/getNode";
import { referenceEnabled } from "../../config";

// UI
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Autocomplete,
  Button,
  InputLabel,
  FormControl,
  TextField,
  Input,
  OutlinedInput,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Stack,
  InputAdornment,
  Typography,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";

const input_font_size = 14;
const input_variant = "outlined";

export default function PostFilters(props) {
  /** redux */
  const entityFiltersReducer = useSelector((state) => state)
    .entityFiltersReducer?.[props.filterEntityType];
  const dispatch = useDispatch();

  const statuses = [
    {
      value: "published",
      label: "Published",
    },
    {
      value: "modified",
      label: "Modified",
    },
    {
      value: "unpublished",
      label: "Draft",
    },
  ];

  const libraries = [
    {
      value: 0,
      label: "All Libraries",
    },
    {
      value: "user_library",
      label: "My Library",
    },
    {
      value: "shared_library",
      label: "Shared Library",
    },
    {
      value: "core_library",
      label: "Corporate Library",
    },
  ];

  const [authors, setAuthors] = useState([]);
  const [authorsError, setAuthorsError] = useState(false);
  const [title, setTitle] = useState(
    entityFiltersReducer?.title ?? props.filters?.title ?? "",
  );
  const [language, setLanguage] = useState(
    entityFiltersReducer.language ?? props.filters?.language ?? 0,
  );
  const [category, setCategory] = useState(
    entityFiltersReducer?.category ?? props.filters?.category ?? 0,
  );
  const [theme, setTheme] = useState(
    entityFiltersReducer?.theme ?? props.filters?.theme ?? 0,
  );

  const [entityId, setEntityId] = useState(
    entityFiltersReducer?.entityId ?? props.filters?.entityId ?? "",
  );
  const [author, setAuthor] = useState(
    entityFiltersReducer?.author ??
      props.filters?.author ?? {
        label: "",
        value: "",
      },
  );
  const [library, setLibrary] = useState(
    entityFiltersReducer?.library ?? props.filters?.library ?? 0,
  );
  const [status, setStatus] = useState(
    entityFiltersReducer?.status ?? props.filters?.status ?? [1, 2, 3],
  );

  // refs
  // making sure debounce callback always uses the latest vars
  const titleRef = useRef(title);
  titleRef.current = title;

  const categoryRef = useRef(category);
  categoryRef.current = category;

  const languageRef = useRef(language);
  languageRef.current = language;

  const themeRef = useRef(theme);
  themeRef.current = theme;

  const entityIdRef = useRef(entityId);
  entityIdRef.current = entityId;

  const authorRef = useRef(author);
  authorRef.current = author;

  const libraryRef = useRef(library);
  libraryRef.current = library;

  const statusRef = useRef(status);
  statusRef.current = status;

  // category select

  useEffect(() => {
    getUsers();
  }, []);

  // const handleTitle = (event) => {
  //   setTitle(event.target.value);
  //   debouncedOnApply();
  // };

  const handleLanguage = (event) => {
    setLanguage(event.target.value);
  };

  // const handleTheme = (event) => {
  //   setTheme(event.target.value);
  // };

  const handleStatus = (event) => {
    const {
      target: { value },
    } = event;
    setStatus(typeof value === "string" ? value.split(",") : value);
  };

  const getUsers = () => {
    getOnlineNode("users")
      .then((response) => {
        let users = [];
        response.data.forEach((item) => {
          users.push({ value: item.uid, label: item.name });
        });
        setAuthors(users);
      })
      .catch((_error) => {
        console.error(_error);
        setAuthorsError(true);
      });
  };

  const onApply = () => {
    const filters = {
      [props.filterEntityType]: {
        title: titleRef.current,
        category: props.selectedCategory,
        author: authorRef.current,
        entityId: entityIdRef.current,
        status: statusRef.current,
        library: libraryRef.current,
        currentPage: 1,
      },
    };

    if (props.redux) {
      dispatch({ type: "UPDATE_ENTITY_FILTERS", payload: filters });
    }
  };

  const onReset = () => {
    setTitle("");
    setLanguage(0);
    setCategory(0);
    setTheme(0);
    setEntityId("");
    setAuthor("");
    setLibrary(0);
    setStatus(["published", "modified", "unpublished"]);

    if (props.redux) {
      dispatch({
        type: "RESET_ENTITY_FILTERS",
        payload: { entityType: props.filterEntityType },
      });
    }
  };

  return (
    <div className="ps-filters">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onApply();
        }}
      >
        <Row className="justify-content-center my-3 py-2">
          <Col xs={12} className="gx-0">
            <Row className="filter-container w-100 align-items-end mx-0">
              <Col xs={12} sm={6} md={4} xl={2} className="filter-item mb-3">
                <FormControl fullWidth size="small">
                  <TextField
                    id="id"
                    label={referenceEnabled ? "ID / Reference" : "ID / SITA"}
                    variant={input_variant}
                    value={entityId}
                    size="small"
                    placeholder={
                      referenceEnabled ? "ID or Reference" : "ID or SITA"
                    }
                    onChange={(event) => {
                      setEntityId(event.target.value);
                    }}
                    InputProps={{
                      style: { fontSize: input_font_size },
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchOutlined
                            sx={{
                              width: "20px",
                              height: "18px",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ style: { fontSize: input_font_size } }}
                    owner="admin"
                    sub_variant="presentation-filter"
                  />
                </FormControl>
              </Col>

              <Col xs={12} sm={6} md={4} xl={2} className="filter-item mb-3">
                <FormControl fullWidth size="small">
                  <Autocomplete
                    ListboxProps={{
                      sx: { fontSize: input_font_size },
                    }}
                    options={
                      authorsError
                        ? [{ value: "", label: "Could not load authors" }]
                        : authors
                    }
                    getOptionDisabled={(option) => option.value === ""}
                    getOptionLabel={(option) =>
                      option?.label ? option?.label : ""
                    }
                    value={author ? author : ""}
                    size="small"
                    freeSolo
                    onChange={(event, value) => {
                      if (value !== null) {
                        setAuthor(value);
                      } else {
                        setAuthor("");
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Author"
                        InputProps={{
                          ...params.InputProps,
                          type: "search",
                          style: {
                            fontSize: input_font_size,
                            paddingRight: "10px",
                          },
                          endAdornment: (
                            <InputAdornment position="start">
                              <SearchOutlined
                                sx={{
                                  width: "20px",
                                  height: "18px",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          style: { fontSize: input_font_size },
                        }}
                        owner="admin"
                        sub_variant="presentation-filter"
                      />
                    )}
                  />
                </FormControl>
              </Col>

              {props.hideStatus !== true && (
                <Col
                  xs={12}
                  sm={6}
                  md={4}
                  xl={2}
                  lg={props.isAddLibrary ? 2 : ""}
                  className="filter-item mb-3"
                >
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ backgroundColor: "primary.light" }}
                  >
                    <InputLabel
                      sx={{
                        fontSize: input_font_size,
                        color: "rgba(0, 0, 0, 0.87)",
                      }}
                      size="small"
                      id="status-select-label"
                    >
                      Status
                    </InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      value={status}
                      multiple
                      label="Status"
                      onChange={handleStatus}
                      renderValue={(selected) => selected.join(", ")}
                      input={
                        input_variant === "outlined" ? (
                          <OutlinedInput
                            owner="admin"
                            sub_variant="presentation-filter"
                            sx={{
                              fontSize: input_font_size,
                            }}
                            label="Status"
                          />
                        ) : (
                          <Input
                            sx={{
                              fontSize: input_font_size,
                              color: "red",
                            }}
                            label="Status"
                          />
                        )
                      }
                    >
                      {statuses.map((state, index) => (
                        <MenuItem key={`status-${index}`} value={state.value}>
                          <Checkbox
                            size="small"
                            checked={status.indexOf(state.value) > -1}
                            sx={{ marginLeft: "-10px", padding: "5px" }}
                          />
                          <ListItemText
                            disableTypography
                            primary={
                              <Typography style={{ fontSize: input_font_size }}>
                                {state.label}
                              </Typography>
                            }
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Col>
              )}

              <Col xs={12} sm={6} md={4} xl={2} className="filter-item mb-3">
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "primary.light" }}
                >
                  <InputLabel
                    sx={{ fontSize: input_font_size, color: "primary.main" }}
                    size="small"
                    id="language-select-label"
                  >
                    Language
                  </InputLabel>
                  <Select
                    labelId="language-select-label"
                    id="language-select"
                    value={language}
                    label="Language"
                    variant={input_variant}
                    onChange={(event) => {
                      handleLanguage(event);
                    }}
                    input={
                      input_variant === "outlined" ? (
                        <OutlinedInput
                          owner="admin"
                          sub_variant="presentation-filter"
                          sx={{
                            fontSize: input_font_size,
                          }}
                          label="Language"
                        />
                      ) : (
                        <Input
                          sx={{ fontSize: input_font_size }}
                          label="Language"
                        />
                      )
                    }
                  >
                    {props?.languages?.map((language, index) => (
                      <MenuItem
                        key={`language-${index}`}
                        value={language.value}
                        sx={{ fontSize: input_font_size }}
                      >
                        {language.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Col>
              <Col xs={12} sm={6} md={4} xl={2} className="filter-item mb-3">
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "primary.light" }}
                >
                  <InputLabel
                    sx={{ fontSize: input_font_size, color: "primary.main" }}
                    size="small"
                    id="library-select-label"
                  >
                    Library
                  </InputLabel>
                  <Select
                    labelId="library-select-label"
                    id="library-select"
                    value={library}
                    label="Library"
                    onChange={(event) => {
                      setLibrary(event.target.value);
                    }}
                    input={
                      input_variant === "outlined" ? (
                        <OutlinedInput
                          owner="admin"
                          sub_variant="presentation-filter"
                          sx={{
                            fontSize: input_font_size,
                          }}
                          label="Library"
                        />
                      ) : (
                        <Input
                          sx={{ fontSize: input_font_size }}
                          label="Library"
                        />
                      )
                    }
                  >
                    {libraries.map((library, index) => (
                      <MenuItem
                        key={`library-${index}`}
                        value={library.value}
                        sx={{ fontSize: input_font_size }}
                      >
                        {library.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Col>
              <Col
                xs={12}
                sm={12}
                md={4}
                xl={2}
                className="filter-item mb-3 align-self-end"
              >
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  alignItems="flex-end"
                >
                  <FormControl className="btn" fullWidth>
                    <Button
                      className="filter-btn"
                      variant="outlined"
                      onClick={onReset}
                    >
                      <Typography owner="admin" variant="button" className="lh">
                        Reset
                      </Typography>
                    </Button>
                  </FormControl>
                  <FormControl className="btn" fullWidth>
                    <Button
                      className="filter-btn"
                      type="submit"
                      sx={{ fontSize: input_font_size }}
                      variant="contained"
                      onClick={() => onApply()}
                    >
                      <Typography owner="admin" variant="button" className="lh">
                        Apply
                      </Typography>
                    </Button>
                  </FormControl>
                </Stack>
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </div>
  );
}
